import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Switch from '@mui/material/Switch';
import EditCommand from './EditCommand';
import Typography from '@mui/material/Typography';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { BaseCommandManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { Command } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Commands/PathItems';
import AISuggestions from '../../components/AISuggestions';



function Commands() {
  const { onError } = useToast();
  const [search, setSearch] = useState('');
  const [filteredCommands, setFilteredCommands] = useState<Command[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCommandName, setNewCommandName] = useState('');
  const [newCommandDescription, setNewCommandDescription] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commandToDelete, setCommandToDelete] = useState<{ id: string, name: string } | null>(null);
  const [editingCommand, setEditingCommand] = useState<string | null>(null);

  const searchCommands = async (query: string) => {
    onError((await Session.getClient().request(BaseCommandManagement.searchCommand({ query, limit: 100, offset: 0 })))
      .maybeOk((value) => setFilteredCommands(value as Command[])));
  };

  useEffect(() => {
    searchCommands('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddCommand = () => {
    setNewCommandName('');
    setNewCommandDescription('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewCommandName('');
    setNewCommandDescription('');
  };

  const handleDialogOk = async () => {
    if (newCommandName.trim()) {
      onError((await Session.getClient().request(BaseCommandManagement.createCommand({ name: newCommandName.trim(), description: newCommandDescription.trim() })))
        .maybeOk(() => searchCommands(search)));
    }
    handleDialogClose();
  };

  const handleDeleteClick = (id: string, name: string) => {
    setCommandToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setCommandToDelete(null);
  };

  const handleDeleteDialogOk = async () => {
    if (commandToDelete) {
      onError((await Session.getCommandClient(commandToDelete.id).request(BaseCommandManagement.deleteCommand()))
        .maybeOk(() => searchCommands(search)));
    }
    handleDeleteDialogClose();
  };

  if (editingCommand) {
    return <EditCommand id={editingCommand} onBack={() => {
      searchCommands(search);
      setEditingCommand(null);
    }} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <TextField
          label="Search commands"
          size="small"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            searchCommands(e.target.value);
          }}
          sx={{ width: 250 }}
        />
        <AISuggestions 
          client={Session.getClient()}
          currentSnapshot={JSON.stringify(filteredCommands)}
          usageInstructions={BaseCommandManagement.toAgentInstructions()}
          developerMessage={'The user is in the view where they can search and see a list of base commands. For any of the operations ' +
            'regarding a specific command the path parameter with the name commandId MUST be specified with that command id.'}
          onOperationsApplied={() => searchCommands(search)}
        />
        <Button variant="contained" color="primary" onClick={handleAddCommand}>Create Command</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight="bold">Name</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Description</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Published</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCommands.map((cmd) => (
              <TableRow key={cmd.id}>
                <TableCell>{cmd.name}</TableCell>
                <TableCell>{cmd.description}</TableCell>
                <TableCell>
                  <Switch checked={cmd.state.state === 'published'} disabled />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => setEditingCommand(cmd.id)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(cmd.id, cmd.name)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Create Command</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Command Name"
            fullWidth
            autoFocus
            value={newCommandName}
            onChange={e => setNewCommandName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            value={newCommandDescription}
            onChange={e => setNewCommandDescription(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Command</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the command "{commandToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteDialogOk} variant="contained" color="error">OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Commands;
